import { useCallback, useRef, useState, type TouchEvent } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@tellescope/design-system";
import { useResizeObserver } from "@tellescope/react-hooks";
import { cn } from "@tellescope/ui";

interface ProductImageCarouselProps {
  /** Used for the image alt text. */
  name: string;
  /** Product photos. When there is one or none, arrows and dots are hidden. */
  images: string[];
  className?: string;
}

const SWIPE_THRESHOLD = 40;
const SWIPE_ENABLED_MAX_WIDTH = 640;

/**
 * Image carousel for a product card. Left/right arrows + dot indicators appear only when there
 * is more than one image. Touch swipe is enabled only when the carousel itself renders narrower
 * than 640px (e.g. a product card on a phone-width layout), not based on the viewport at large.
 */
export const ProductImageCarousel = ({ name, images, className }: ProductImageCarouselProps) => {
  const count = Math.max(1, images.length);
  const hasMultiple = count > 1;

  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const { ref: containerRef, value: swipeEnabled } = useResizeObserver<boolean, HTMLDivElement>(
    (element, entry) => (entry?.contentRect.width ?? element.getBoundingClientRect().width) < SWIPE_ENABLED_MAX_WIDTH,
    false
  );

  const go = useCallback((next: number) => setIndex(((next % count) + count) % count), [count]);

  const onTouchStart = (e: TouchEvent) => {
    if (!swipeEnabled || !hasMultiple) return;
    const touch = e.touches[0];
    if (touch) touchStartX.current = touch.clientX;
  };

  const onTouchEnd = (e: TouchEvent) => {
    if (!swipeEnabled || !hasMultiple || touchStartX.current === null) return;
    const touch = e.changedTouches[0];
    if (touch) {
      const dx = touch.clientX - touchStartX.current;
      if (Math.abs(dx) > SWIPE_THRESHOLD) go(dx < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  return (
    <div
      ref={containerRef}
      className={cn("relative h-48 overflow-hidden bg-muted", className)}
      role="group"
      aria-roledescription="carousel"
      aria-label={`${name} photos`}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="flex h-full transition-transform duration-300 ease-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {images.map((src, i) => (
          <div key={src} className="relative h-full w-full flex-shrink-0 bg-muted" aria-hidden={i !== index}>
            <img
              src={src}
              alt={`${name}${count > 1 ? ` photo ${i + 1} of ${count}` : ""}`}
              loading={i === 0 ? "eager" : "lazy"}
              className="h-full w-full object-cover"
            />
          </div>
        ))}
      </div>

      {hasMultiple && (
        <>
          <Button
            variant="ghost"
            iconOnly
            hoverLabel="Previous image"
            data-test="carousel-prev"
            onClick={() => go(index - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-card/70 backdrop-blur-md text-foreground shadow-sm hover:bg-card/90"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            iconOnly
            hoverLabel="Next image"
            data-test="carousel-next"
            onClick={() => go(index + 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-card/70 backdrop-blur-md text-foreground shadow-sm hover:bg-card/90"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </>
      )}

      {hasMultiple && (
        <div
          data-test="carousel-dots"
          className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5"
        >
          {images.map((src, i) => (
            <button
              type="button"
              key={src}
              aria-label={`Go to image ${i + 1}`}
              aria-current={i === index}
              onClick={() => go(i)}
              className={cn(
                "h-1.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                i === index ? "w-4 bg-primary" : "w-1.5 bg-foreground/30 hover:bg-foreground/50"
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};
